<template>
	<div class="cassie-vertical-md mt-1">
		<FilterBar
			:search-query.sync="cookieTextTemplateSearchQuery"
			search-query-label="Text Template Name"
			:selected-brand-id.sync="selectedAdminPortalBrandId"
			@persistSearchQuery="changeCookieTextTemplateSearchQuery"
			@persistSelectedBrandId="changeAdminPortalBrandFilter"
		>
			<template #action>
				<PrimaryActionButton
					v-if="userCanCreateUpdate"
					@click="onCreateClick()"
				>
					<v-icon left>
						mdi-plus
					</v-icon>
					Create
				</PrimaryActionButton>
			</template>
		</FilterBar>
		<SectionCard>
			<template #title>
				Text Templates
			</template>
			<template #body>
				<DataTable
					:headers="tableHeaders"
					:items="filteredCookieTextTemplates"
					sort-by="id"
					:sort-desc="true"
					@click:row="onViewClick"
				>
					<template #item.name="{ item }">
						{{ item.name }}
						<Chip
							v-if="item.isSystem"
							class="ml-2 prussian-blue white--text"
						>
							Cassie Template
						</Chip>
					</template>
					<template #item.action="{ item }">
						<v-menu
							offset-y
							left
						>
							<template #activator="{ on, attrs }">
								<v-btn
									icon
									small
									v-bind="attrs"
									v-on="on"
									@click.stop
								>
									<v-icon>
										mdi-dots-horizontal
									</v-icon>
								</v-btn>
							</template>
							<v-list dense>
								<v-list-item
									v-if="!item.isSystem && userCanCreateUpdate && userHasBrand(item.brandId)"
									@click="onEditClick(item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											Edit
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-else
									@click="onViewClick(item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											View
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="(userCanCreateUpdate && userHasBrand(item.brandId)) || item.isSystem"
									@click.stop.prevent="onCloneClick(item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											Clone
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="userCanDelete && item.usage === 0 && !item.isSystem && userHasBrand(item.brandId)"
									@click.stop.prevent="cookieTextTemplateToDelete = item"
								>
									<v-list-item-content>
										<v-list-item-title>
											Delete
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</v-menu>
					</template>
				</DataTable>
			</template>
		</SectionCard>
		<ConfirmDeleteModal
			v-if="cookieTextTemplateToDelete && userCanDelete"
			:entity-name="cookieTextTemplateToDelete.name"
			entity-type="Text Template"
			@close="cookieTextTemplateToDelete = null"
			@delete="deleteCookieTextTemplate"
		/>
	</div>
</template>
<script>
import FilterBar from '../../../../../../../shared/components/filter-bar.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import DataTable from '../../../../../../../shared/components/data-table.vue'
import ConfirmDeleteModal from '../../../../../../../shared/components/confirm-delete-modal.vue'
import Chip from '../../../../../../../shared/components/chip.vue'
import { useLabels } from '../../../../../../../shared/state/admin-portal-navigation.js'
import { selectedAdminPortalBrandId, changeAdminPortalBrandFilter, userHasBrand } from '../../../../../../../shared/state/brands.js'
import { MANAGE_COOKIE_TEXT_TEMPLATE } from '../../../../../router/route-names.js'
import { deleteCookieTextTemplate } from '../../../../../../../shared/utils/api/cookies/cookie-text-templates.js'
import { cookieTextTemplateSearchQuery, changeCookieTextTemplateSearchQuery } from '../../../../../../../shared/state/cookie-text-templates.js'
import { showSnackbar } from '../../../../../../../shared/state/snackbar.js'

export default {
	components: {
		FilterBar,
		PrimaryActionButton,
		SectionCard,
		DataTable,
		ConfirmDeleteModal,
		Chip
	},
	props: {
		cookieTextTemplates: Array,
		userCanCreateUpdate: Boolean,
		userCanDelete: Boolean
	},
	setup () {
		return {
			userHasBrand,
			cookieTextTemplateSearchQuery,
			changeCookieTextTemplateSearchQuery,
			selectedAdminPortalBrandId,
			changeAdminPortalBrandFilter
		}
	},
	data () {
		return {
			cookieTextTemplateToDelete: null
		}
	},
	computed: {
		tableHeaders () {
			return [
				{ value: 'id', text: 'ID', width: '6%' },
				{ value: 'brandName', text: useLabels('Brand'), width: '16%' },
				{ value: 'name', text: 'Text Template Name', width: '44%' },
				{ value: 'usage', text: 'Usage', width: '12%' },
				{ value: 'createdDate', text: 'Created Date', width: '22%' },
				{ value: 'action', text: 'Action', width: '80px', align: 'center', sortable: false }
			]
		},
		filteredCookieTextTemplates () {
			return this.cookieTextTemplates.filter(({ name, brandId }) => {
				let check = true
				if (cookieTextTemplateSearchQuery.value) check = name.toLowerCase().includes(cookieTextTemplateSearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && brandId === selectedAdminPortalBrandId.value
				return check
			}).map(textTemplate => ({
				...textTemplate
			}))
		}
	},
	created () {
		this.$emit('load-cookie-text-templates')
	},
	methods: {
		onCreateClick () {
			this.$router.push({
				name: MANAGE_COOKIE_TEXT_TEMPLATE
			})
		},
		onEditClick (item) {
			this.$router.push({
				name: MANAGE_COOKIE_TEXT_TEMPLATE,
				params: {
					cookieTextTemplateToEdit: item
				}
			})
		},
		onViewClick (item) {
			this.$router.push({
				name: MANAGE_COOKIE_TEXT_TEMPLATE,
				params: {
					cookieTextTemplateToEdit: item,
					readOnly: true
				}
			})
		},
		onCloneClick (item) {
			const cookieTextTemplateToEdit = JSON.parse(JSON.stringify(item))
			cookieTextTemplateToEdit.name = cookieTextTemplateToEdit.name + ' (clone)'
			this.$router.push({
				name: MANAGE_COOKIE_TEXT_TEMPLATE,
				params: {
					cookieTextTemplateToEdit,
					cloneMode: true
				}
			})
		},
		async deleteCookieTextTemplate () {
			await deleteCookieTextTemplate(this.cookieTextTemplateToDelete.id)
			showSnackbar('You have removed this text template')
			this.cookieTextTemplateToDelete = null
			this.$emit('load-cookie-text-templates')
		}
	}
}
</script>
